import { z } from 'zod/v4';
import { PlanVisibility } from '../types/plans.types';

export const PLAN_VISIBILITIES = Object.values(PlanVisibility);

/**
 * Un límite vacío no es cero. El campo convertía `''` en 0 en silencio y el plan se creaba
 * bloqueando esa funcionalidad para quien lo contratara, sin que el administrador lo hubiera
 * escrito.
 *
 * El cero sí se acepta cuando se escribe a propósito: la API lo permite
 * (`GreaterThanOrEqualTo(0)`) y un nivel sin reportes programados es una configuración válida.
 */
const limitSchema = (label: string) =>
  z
    .number({
      error: (issue) =>
        issue.input === undefined ? `${label} es obligatorio` : `${label} debe ser un número`,
    })
    .int(`${label} debe ser un número entero`)
    .min(0, `${label} no puede ser negativo`);

/**
 * Los topes son los mismos que valida la API (`MaximumLength(100)` y `MaximumLength(500)` en
 * CreatePlan y UpdatePlanDetails). Sin ellos aquí, el formulario enviaba la petición, el backend
 * la rechazaba con 400 y el administrador solo veía un toast genérico: no había forma de saber
 * qué campo fallaba ni cuál era el límite.
 *
 * El recorte va antes del tope para medir lo mismo que se manda, no los espacios de los extremos.
 */
export const PLAN_NAME_MAX = 100;
export const PLAN_DESCRIPTION_MAX = 500;

const nameSchema = z
  .string()
  .trim()
  .min(1, 'El nombre es requerido')
  .max(PLAN_NAME_MAX, `El nombre no puede superar los ${PLAN_NAME_MAX} caracteres`);

const descriptionSchema = z
  .string()
  .trim()
  .max(
    PLAN_DESCRIPTION_MAX,
    `La descripción no puede superar los ${PLAN_DESCRIPTION_MAX} caracteres`,
  )
  .optional();

const priceSchema = (label: string) =>
  z
    .number({
      error: (issue) =>
        issue.input === undefined ? `${label} es obligatorio` : `${label} debe ser un número`,
    })
    .positive(`${label} debe ser mayor a cero`);

/** Los siete límites que una suscripción congela al contratar. */
const planLimitsSchema = {
  maxUsers: limitSchema('El límite de usuarios'),
  maxRegions: limitSchema('El límite de regiones'),
  maxActiveTopics: limitSchema('El límite de temas activos'),
  maxActiveActors: limitSchema('El límite de actores activos'),
  maxAiAnalysisMonthly: limitSchema('El límite de análisis de IA'),
  maxAlerts: limitSchema('El límite de alertas'),
  maxScheduledReports: limitSchema('El límite de reportes programados'),
};

export const createPlanSchema = z.object({
  name: nameSchema,
  description: descriptionSchema,
  priceMonthly: priceSchema('El precio mensual'),
  priceAnnual: priceSchema('El precio anual'),
  visibility: z.enum(PLAN_VISIBILITIES),
  ...planLimitsSchema,
});

/**
 * Editar un plan no toca sus precios: eso exige un precio nuevo en Stripe y puede cambiarle
 * el cobro a las cuentas suscritas, así que va por su propio endpoint y su propio formulario.
 */
export const editPlanLimitsSchema = z.object({
  name: nameSchema,
  description: descriptionSchema,
  ...planLimitsSchema,
});

export const updatePlanPricingSchema = z.object({
  priceMonthly: priceSchema('El precio mensual'),
  priceAnnual: priceSchema('El precio anual'),
  applyToExistingSubscribers: z.boolean(),
});

export type CreatePlanFormValues = z.infer<typeof createPlanSchema>;
export type UpdatePlanPricingFormValues = z.infer<typeof updatePlanPricingSchema>;
export type EditPlanLimitsFormValues = z.infer<typeof editPlanLimitsSchema>;
